import {
  ABU,
  AMap,
  buildSubView,
  carImages,
  Em,
  MapItemContent,
  modalConfirm,
  playContext,
  showMsg,
  TestPage,
  TItemData,
} from "@client";
import { Style } from "@client/style";
import { NpcModel } from "@npc";
import { calcScore, NumberUtil, SceneConst, SubNamespace } from "@share";
import { Button, Checkbox, Input, ModalFuncProps, Radio } from "antd";
import React, { useContext, useState } from "react";

const nameSuffixData = ["高新路体验店", "4S店", "体验中心", "汽车销售服务中心", "汽车服务有限公司", "高新区专营店"];

function Play({ playDone }: { playDone: () => void }) {
  const sceneContext = useContext(playContext);
  const {
    saveResult,
    params: { searchCost },
    playerState: { carList },
  } = sceneContext;
  const { cars } = SceneConst.buyCar;
  const [seenList, setSeenList] = useState(cars.map(() => false)),
    setSeen = (i: number) => {
      const _seenList = seenList.slice();
      _seenList[i] = true;
      setSeenList(_seenList);
    };
  const [tradeIndex, setTradeIndex] = useState<number>(null!);
  const items: TItemData[] = cars.map(({ name }, i) => ({
    name: `${name}${nameSuffixData[carList[i].nameSuffix]}【${carList[i].price}万】`,
    seen: seenList[i],
  }));
  const seeItem = (i: number) => {
    if (tradeIndex != null) {
      return;
    }
    const { name } = SceneConst.buyCar.cars[i],
      { price, discount, utility } = carList[i],
      seen = seenList[i];
    const confirmProps: ModalFuncProps = {
      onOk: () => {
        const range = NumberUtil.normalizeUniformDist(utility, SceneConst.buyCar.utility),
          msg: string[] = {
            [NumberUtil.Range.I]: ["刚好够用", "平淡无奇", "满足基本要求"],
            [NumberUtil.Range.II]: ["动力平顺", "空间宽敞", "乘坐舒适"],
            [NumberUtil.Range.III]: ["相当满意", "超出预期", "给了你一丝惊喜"],
            [NumberUtil.Range.IV]: ["非常满意", "爱不释手", "激动万分"],
          }[range],
          desc = `通过试驾，你对该车的印象是【${
            msg[~~(Math.random() * msg.length)]
          }】，你给出的效用评分为【${utility}】万`;
        const confirmProps: ModalFuncProps = {
          okText: "购买",
          cancelText: "离开",
          onOk: async () => {
            setTradeIndex(i);
            const totalSearchCost = seenList.map((seen) => (seen ? searchCost : 0)).reduce((m, n) => m + n),
              tradePrice = seen ? price : +(price - discount).toFixed(1);
            await showMsg(
              <p>
                {!!totalSearchCost && (
                  <>
                    花费<Em>{totalSearchCost}</Em>的搜寻成本后，
                  </>
                )}
                你本轮以
                <Em>{tradePrice}万</Em>的价格买到效用为
                <Em>{utility}万</Em>的<Em>{name}</Em>牌汽车，产生的净收益为
                <Em>{(utility - tradePrice).toFixed(1)}万</Em>。
              </p>,
              {
                okText: "知道了",
              }
            );
            const prices = carList.map((t) => t.price),
              disCountPrices = carList.map(({ price, discount }) => price - discount);
            saveResult({
              score: calcScore(NumberUtil.getMax(prices), tradePrice, NumberUtil.getMin(disCountPrices)),
              seenList,
              tradeIndex,
            });
            playDone();
          },
          onCancel: () => setSeen(i),
          title: name,
          maskStyle: {
            backgroundImage: `url('${carImages[i]}')`,
            filter: "blur(.5rem)",
          },
          content: (
            <MapItemContent
              desc={desc}
              data={[
                {
                  label: "价格",
                  value: `${price}万`,
                },
                {
                  label: "限时折扣价",
                  value: discount && !seen ? `${(price - discount).toFixed(1)}万` : "无折扣",
                },
                {
                  label: "效用评分",
                  value: `${utility}万`,
                },
              ]}
            />
          ),
        };
        modalConfirm(confirmProps);
      },
      cancelText: "取消",
      content: searchCost ? (
        <p>
          你选择了
          <Em>{name}</Em>，是否支付
          <Em>{searchCost}</Em>元的搜寻成本查看该品牌汽车信息?
        </p>
      ) : (
        <p>
          你选择了
          <Em>{name}</Em>，本次搜寻无成本。
        </p>
      ),
    };
    modalConfirm(confirmProps);
  };
  return <AMap items={items} onClick={(i) => seeItem(i)} />;
}

function Test({ done }: { done: (answers, answerScores) => void }) {
  return (
    <TestPage
      questions={[
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                在限时优惠下，如果买家在搜寻后返回，他往往需要支付更高的价格。假设卖家设定的产品价格是10000元，如果首次访问时购买，优惠1000元，买家当前的产品效用的货币价值是11000元，以下哪些情形中，买家应继续搜寻：（10分，多选）
              </p>
              <Checkbox.Group {...inputProps({ margin: ".5rem" })}>
                <Checkbox value={1}>A.期望（净）效用=2000，搜寻成本=900</Checkbox>
                <br />
                <Checkbox value={2}>B.期望（净）效用=3000，搜寻成本=900</Checkbox>
                <br />
                <Checkbox value={3}>C.期望（净）效用=4000，搜寻成本=1000</Checkbox>
                <br />
                <Checkbox value={4}>D.期望（净）效用=2000，搜寻成本=500</Checkbox>
              </Checkbox.Group>
            </div>
          ),
          Answer: () => (
            <p>当下净效用为11000-9000=2000。当效用增量大于搜寻成本时，买家应该选择搜寻。综上，答案选择B、C</p>
          ),
          answer: [[2, 3]],
        },
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                在经典搜寻中（无限时优惠），假设所有卖家设定的产品价格是9000元，买家当前的产品效用的货币价值是11000元。买家预期未来搜寻过程中，产品效用的货币价值可能在9000-15000元中均匀分布，则此刻买家的期望效用为：
              </p>
              <Radio.Group {...inputProps({ margin: ".5rem" })}>
                <Radio value={1}>A.1000</Radio>
                <Radio value={2}>B.2000</Radio>
                <Radio value={3}>C.3000</Radio>
                <Radio value={4}>D.4000</Radio>
              </Radio.Group>
            </div>
          ),
          Answer: () => <img src={`${ABU}answer/4.2.png`} />,
          answer: [3],
        },
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                在限时优惠下，如果买家在搜寻后返回，他往往需要支付更高的价格。假设所有卖家设定的产品价格都是10000元，如果首次访问时购买，优惠1000元(即价格为9000元)，买家当前的产品效用的货币价值是11000元。买家预期未来搜寻过程中，产品效用的货币价值可能在9000-15000元中均匀分布，则此刻买家的期望效用为：
              </p>
              <Radio.Group {...inputProps({ margin: ".5rem" })}>
                <Radio value={1}>A.347</Radio>
                <Radio value={2}>B.1000</Radio>
                <Radio value={3}>C.1347</Radio>
                <Radio value={4}>D.2347</Radio>
              </Radio.Group>
            </div>
          ),
          Answer: () => <img src={`${ABU}answer/4.3.png`} />,
          answer: [1],
        },
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                主观题：假设有一笔报酬，你可以选择现在接受，或者在1个月以后接受，两个时间点所获得的报酬不一样，请在表格中打勾。（该报酬没有任何风险问题，不存在1个月以后无法拿到报酬的可能。）
              </p>
              <ul>
                {[
                  ["A.\t现在接受110元", "B.\t1个月以后接受200元"],
                  ["A.\t现在接受120元", "B.\t1个月以后接受200元"],
                  ["A.\t现在接受130元", "B.\t1个月以后接受200元"],
                  ["A.\t现在接受140元", "B.\t1个月以后接受200元"],
                  ["A.\t现在接受150元", "B.\t1个月以后接受200元"],
                  ["A.\t现在接受160元", "B.\t1个月以后接受200元"],
                  ["A.\t现在接受170元", "B.\t1个月以后接受200元"],
                  ["A.\t现在接受180元", "B.\t1个月以后接受200元"],
                  ["A.\t现在接受190元", "B.\t1个月以后接受200元"],
                ].map(([optionA, optionB]) => (
                  <li style={{ listStyle: "decimal" }}>
                    <Radio.Group {...inputProps({ margin: ".5rem" })}>
                      <Radio value={1}>{optionA}</Radio>
                      <Radio value={2}>{optionB}</Radio>
                    </Radio.Group>
                  </li>
                ))}
              </ul>
            </div>
          ),
        },
        {
          isAnalysis: true,
          Content: ({ inputProps }) => (
            <div>
              <p>请结合本次任务数据， 谈谈风险偏好、态度、心理因素是如何影响搜寻决策的？无正确的答案，只需说明理由。</p>
              <br />
              <Input.TextArea maxLength={2e3} rows={4} {...inputProps({ width: "100%" })} />
            </div>
          ),
        },
      ]}
    />
  );
}

function BuyCarIntro6th() {
  const [n, setN] = useState(0),
    [v, setV] = useState(0);
  return (
    <div>
      6.体验随机分布：你可通过点击以下【体验搜寻】按钮，体验【效用评分】【U】的（随机）变化。
      <table className={Style.tableInIntro}>
        <tbody>
          <tr>
            <td rowSpan={2}>
              第<Em>{n}</Em>次搜寻
            </td>
            <td>你对本次试驾车辆的效用评分为</td>
          </tr>
          <tr>
            <td>
              <Em>{v}</Em>万
            </td>
          </tr>
        </tbody>
      </table>
      <p>
        效用评分随机、均匀地分布在
        <Em>
          {SceneConst.buyCar.utility.MIN}，{SceneConst.buyCar.utility.MAX}
        </Em>
        万之间。
      </p>
      <div style={{ margin: "1rem", textAlign: "right" }}>
        <Button
          className={Style.btnPrimarySm}
          onClick={() => {
            setN(n + 1);
            setV(NumberUtil.uniformDist(SceneConst.buyCar.utility, 1));
          }}
        >
          体验搜寻
        </Button>
      </div>
    </div>
  );
}

export const BuyCarView = buildSubView({
  namespace: SubNamespace.BuyCar,
  npcModel: NpcModel.BuyCar,
  label: "买车",
  withSearch: true,
  npcWelcome: async () => {
    await showMsg("你的任务是在接下来的时间里，购买一辆代步车。");
  },
  introMsg: ({ searchCost }) => {
    return [
      ...`1.\t你需要一辆代步汽车增加你的通勤效率。
2.\t经过调研，你将从六个备选品牌中逐个搜寻并选择让你最满意的选项。
3.\t你在搜寻过程中会产生成本：每次搜寻需要花费X元。
4.\t你并不清楚每个品牌的汽车的驾驶感受：有的车动力较好，但油耗、舒适度可能打折扣；有的车较舒适宽敞，但动力上让你觉得不满意。只有通过搜寻和试驾，你才能对具体车型选项打出【效用评分U】。`
        .trim()
        .split("\n"),
      <p>
        5.经过之前的乘车经历，你对不同品牌的效用评分有一个大致的预期，其随机、均匀地分布在
        <Em>
          {SceneConst.buyCar.utility.MIN}，{SceneConst.buyCar.utility.MAX}万
        </Em>
        之间。
      </p>,
      <BuyCarIntro6th />,
      "7.\t销售商可能会使用限时优惠的方式进行促销，你需要决定是否立即购买，并接受价格优惠，或者延迟决定，但需要支付更高的价格。",
      <p>
        8. 你可以通过点击【图标
        <span
          style={{
            display: "inline-block",
            width: "2.5rem",
            height: "2.5rem",
            background: "url(/MarketSearch/static/asset/img/poi-marker.png) 0 -2.5rem / 27rem no-repeat",
          }}
        />
        】获得该车辆的具体效用评分。你需要决定是否接受车辆的报价，或者继续搜寻。
      </p>,
      `9.\t你可以返回之前搜索过的产品。`,
    ];
  },
  Play,
  Test,
  initState: (s) =>
    (s.carList = SceneConst.buyCar.cars.map(() => ({
      nameSuffix: ~~(Math.random() * nameSuffixData.length),
      price: NumberUtil.uniformDist(SceneConst.buyCar.price, 1),
      discount: NumberUtil.uniformDist(SceneConst.buyCar.discount, 1),
      utility: NumberUtil.uniformDist(SceneConst.buyCar.utility, 1),
    }))),
});
